/**
 * The seven Learning Center tabs (plan section 13, G13) and the shape of the
 * Team and Manage sub-view registries. This file is the single source for each
 * tab's route path, nav label, title, one-line description and admin flag:
 * TabNav (the links), LmsApp (the routes) and every placeholder screen read it,
 * so the URL, the nav and the on-screen copy cannot drift apart.
 *
 * Hot file (plan 15.12): WP-03 only. A later WP never edits it; a tab WP
 * replaces its own tabs/<key>/<Key>Tab.tsx, and a sub-view WP replaces the one
 * file its registry entry points at (tabs/team/subviews.ts,
 * tabs/manage/subviews.ts).
 *
 * Metadata only, no components: the tab screens import this file for their
 * title and description, so importing them here would be a cycle.
 */
import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Award, BookOpen, FileText, Home, ListChecks, SlidersHorizontal, Users } from 'lucide-react';

export type LmsTabKey = 'home' | 'courses' | 'records' | 'sops' | 'onboarding' | 'team' | 'manage';

export interface LmsTabConfig {
  key: LmsTabKey;
  /** Path segment under /lms/ ('' is the index route, /lms itself). */
  path: string;
  /** Short label for the tab nav. */
  navLabel: string;
  /** Title for the tab's PageHeader. */
  title: string;
  /** One line under the title (plan section 13, written as screen copy). */
  description: string;
  icon: LucideIcon;
  /**
   * Plan section 13: Team and Manage render only when GET /api/lms/me says
   * isLmsAdmin; every admin route is also enforced server-side (requireLmsAdmin).
   */
  adminOnly: boolean;
  /**
   * The tab hosts SegmentedTabs sub-views addressed by URL
   * (/lms/team/<subview>/...), so its route matches the whole subtree.
   */
  hasSubviews: boolean;
}

// Descriptions are double-quoted on purpose: an apostrophe inside a
// single-quoted string breaks the whole file (common.md hazard).
export const LMS_TABS: LmsTabConfig[] = [
  {
    key: 'home',
    path: '',
    navLabel: 'Home',
    title: 'Home',
    // plan 7.1
    description: "Your to-do list, the courses you have in progress, your recent completions, your inbox and your onboarding progress.",
    icon: Home,
    adminOnly: false,
    hasSubviews: false,
  },
  {
    key: 'courses',
    path: 'courses',
    navLabel: 'Courses',
    title: 'Courses',
    // plan section 13 tab 2
    description: "The catalog by category with your status on each course. Open a course to see its overview and start or continue it.",
    icon: BookOpen,
    adminOnly: false,
    hasSubviews: false,
  },
  {
    key: 'records',
    path: 'records',
    navLabel: 'Records',
    title: 'Records',
    // plan 7.5
    description: "Every completion and certificate you hold, current and superseded, your training log, and the certificates you earned elsewhere.",
    icon: Award,
    adminOnly: false,
    hasSubviews: false,
  },
  {
    key: 'sops',
    path: 'sops',
    navLabel: 'SOPs',
    title: 'SOPs',
    // plan 11.1
    description: "The standard operating procedures with their effective versions and training status. Open the current file of any SOP.",
    icon: FileText,
    adminOnly: false,
    hasSubviews: false,
  },
  {
    key: 'onboarding',
    path: 'onboarding',
    navLabel: 'Onboarding',
    title: 'Onboarding',
    // plan 12.3
    description: "Your onboarding checklist: the things to do outside the platform, who does each one, and what is still open.",
    icon: ListChecks,
    adminOnly: false,
    hasSubviews: false,
  },
  {
    key: 'team',
    path: 'team',
    navLabel: 'Team',
    title: 'Team',
    // plan section 13 tab 6
    description: "Compliance matrix, people, sign-offs, external certificates, onboarding runs, reports and certificate verification.",
    icon: Users,
    adminOnly: true,
    hasSubviews: true,
  },
  {
    key: 'manage',
    path: 'manage',
    navLabel: 'Manage',
    title: 'Manage',
    // plan section 13 tab 7
    description: "The catalog and its versions, groups and profiles, assigning training, campaigns, onboarding templates and settings.",
    icon: SlidersHorizontal,
    adminOnly: true,
    hasSubviews: true,
  },
];

/** The config entry for a tab key (throws on an unknown key: G3, never a silent default). */
export function lmsTab(key: LmsTabKey): LmsTabConfig {
  const tab = LMS_TABS.find((t) => t.key === key);
  if (!tab) throw new Error(`Unknown Learning Center tab: ${key}`);
  return tab;
}

/** The absolute URL of a tab (/lms for Home, /lms/<path> otherwise). */
export function lmsTabHref(tab: LmsTabConfig): string {
  return tab.path ? `/lms/${tab.path}` : '/lms';
}

/**
 * One entry of a Team or Manage sub-view registry (plan section 13 tabs 6
 * and 7). The sub-view renders under the host tab's PageHeader and
 * SegmentedTabs at /lms/<tab>/<path>; anything deeper in the URL
 * (/lms/team/people/<userId>) belongs to the sub-view, which may declare its
 * own relative <Routes> (they resolve under /lms/<tab>/<path>/).
 */
export interface LmsSubview {
  /** URL segment under the host tab, also the SegmentedTabs value. */
  path: string;
  /** SegmentedTabs label. */
  label: string;
  /** One line shown under the host tab's title while this sub-view is active. */
  description: string;
  icon: LucideIcon;
  /** The screen: a default-exported component with no required props. */
  Component: ComponentType;
  /** The work package that replaces the placeholder file (plan 15.6). */
  ownerWp: string;
}
